/**
 * @override
 * Custom implementation of UgcBlock component for King Living
 * Overrides the autogenerated skeleton from @amblique/contentful
 *
 * Initializes Pixlee widget with lazy loading support.
 * Widget only loads when scrolled into view for better performance.
 *
 * @see https://developers.pixlee.com/docs/pixlee-widget-user-guide#lazy-loading
 */

import PropTypes from 'prop-types';

import {Box, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {usePixleeWidget} from '@amblique/king-living/app/hooks/use-pixlee-widget';

/**
 * UgcBlock component - Renders Pixlee UGC widget container with lazy loading
 * Widget initializes only when scrolled into viewport (within 200px)
 *
 * @param {Object} props - Component props
 * @param {Contentful.UgcBlock} props.component - UgcBlock component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {boolean} [props.lazyLoad=true] - Enable lazy loading (default: true)
 * @returns {React.ReactElement|null}
 */
export const UgcBlock = ({component, isComplete = true, isFetching = false, lazyLoad = true, ...props}) => {
  const {title, bannerId} = component || {};

  // Initialize Pixlee widget with lazy loading
  // Hook handles all the complexity of script loading, intersection observer, and widget initialization
  const {isReady, containerRef, containerId} = usePixleeWidget(bannerId, {
    lazyLoad,
    rootMargin: '200px', // Preload when within 200px of viewport
  });

  // Get theme styles
  const styles = useMultiStyleConfig('contentful/ugc-block', {
    component,
    isComplete,
    isFetching,
    isReady,
  });

  // Don't render if missing required bannerId
  if (!bannerId) {
    console.warn('UgcBlock: Missing required bannerId field');

    return null;
  }

  // Show loading/incomplete state if data is being fetched or not complete
  if (isFetching || !isComplete) {
    return (
      <Box className="ugc-block ugc-block--loading" {...styles.root} {...props}>
        <Skeleton {...styles.skeleton} />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="ugc-block"
      data-banner-id={bannerId}
      data-lazy-load={lazyLoad}
      data-title={title}
      data-widget-ready={isReady}
      {...styles.root}
      {...props}
    >
      {/* Show skeleton while waiting for widget to load */}
      {!isReady && (
        <Skeleton
          endColor="gray.200"
          startColor="gray.100"
          {...styles.skeleton}
        />
      )}

      {/* Pixlee container - widget will be injected here by Pixlee script */}
      {/* Each widget gets a unique container ID to support multiple widgets on same page */}
      <Box
        id={containerId}
        {...styles.container}
      />
    </Box>
  );
};

UgcBlock.displayName = 'UgcBlock';
UgcBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  lazyLoad: PropTypes.bool,
};

export default UgcBlock;
